import { IConfig } from "@kuika/kuika-cl-model";
import { Rule } from "antd/es/form";
import { KuikaBadgeColor } from "../../ui-kit/kuika-badge/kuika-badge";

/**
 * The Config Manager's list rules, kept out of the components so the two surfaces that
 * render the list — the project's Config Manager and the workspace's Global
 * Configurations — cannot drift apart, and so the name rule create and rename share is
 * one testable thing rather than two regexes in two files.
 */

/**
 * Letters, digits and spaces. This is the LANDING page's historical rule verbatim, which
 * is the looser of the two the feature carried: the editor's rename demanded a leading
 * letter, so a configuration created as "2024 Prod" could never be re-saved under its own
 * name. Taking the looser rule for both means nothing that used to be creatable stops
 * being creatable.
 */
export const CONFIG_NAME_PATTERN: RegExp = /^[A-Za-z0-9 ]+$/;
export const CONFIG_NAME_MESSAGE: string = "Configuration name can only contain English letters, numbers and spaces.";
export const CONFIG_NAME_CONFLICT_MESSAGE: string = "This Config Name is used before.";
export const CONFIG_NAME_REQUIRED_MESSAGE: string = "Please enter a name.";

/**
 * Not a storage limit — the name columns are unbounded, unlike the email template's, whose 50
 * mirrors its column. It is a product limit: a name is shown in the editor header next to
 * the platform tabs, in the publish popover's selector and in the list, and every one of
 * those truncates it. 100 leaves room for a descriptive name ("QA EU region 2024 hotfix")
 * without letting a pasted paragraph become a configuration.
 */
export const CONFIG_NAME_MAX_LENGTH: number = 100;
export const CONFIG_NAME_LENGTH_MESSAGE: string = `Configuration name can be at most ${CONFIG_NAME_MAX_LENGTH} characters.`;

/**
 * The one place a name is trimmed before it is checked or saved. A name box does not trim
 * as you type, so without this " CMTest01" and "CMTest01" looked like two different
 * configurations to the uniqueness check, and a name typed as all spaces looked non-empty
 * to `required`.
 */
export const normalizeConfigName = (name: string): string => name.trim();

/**
 * A configuration's name, defensively.
 *
 * `IConfig.name` is typed `string`, but the backend can hand back `null` — a row saved
 * before the name was required, or by a client that dropped it. Every comparison here runs
 * over the whole list, so one such row used to take the search and the uniqueness validator
 * down with a `toLowerCase` of null.
 */
export const nameOf = (config: IConfig): string => config.name ?? "";

/**
 * A configuration's name as a test id fragment. Spaces go — an id with a space in it is
 * awkward to address, and every other migrated panel strips them the same way — and a
 * nameless row yields an empty fragment rather than throwing.
 */
export const configTestName = (config: IConfig): string => nameOf(config).replace(/\s/g, "");

/** Which store a configuration belongs to. The project list shows both; the global one, one. */
export type ConfigScope = "project" | "global";

/**
 * What the Platforms and Scope columns are painted with.
 *
 * Four platforms and two scopes, each with a colour of its own so a row can be read at a
 * glance rather than by reading four identical grey pills. They live here — beside the
 * platform list itself — so a surface that grows a fifth platform, or a second list that
 * shows the same badges, takes the colour with the data instead of picking its own.
 *
 * The two sets are deliberately disjoint: the scope is a classification, not a platform,
 * and sharing a colour across the two columns would suggest a relation that is not there.
 */
export const CONFIG_SCOPE_COLOUR: Record<ConfigScope, KuikaBadgeColor> = {
  project: "grey",
  global: "yellow"
};

export interface IConfigPlatform {
  key: string;
  label: string;
  /** The badge colour. One per platform, and one place they come from — see `CONFIG_SCOPE_COLOUR`. */
  color: KuikaBadgeColor;
}

/**
 * A configuration is an environment profile, not a key/value row: what a list can show
 * about one is its name, the platforms it publishes and whether it is the selected one.
 */
export const getConfigPlatforms = (config: IConfig): IConfigPlatform[] => {
  const platforms: IConfigPlatform[] = [];
  if (config.supportWeb) platforms.push({ key: "web", label: "Web", color: "blue" });
  if (config.supportAndroid) platforms.push({ key: "android", label: "Android", color: "green" });
  if (config.supportIOS) platforms.push({ key: "ios", label: "iOS", color: "purple" });
  if (config.supportElectron) platforms.push({ key: "electron", label: "Desktop", color: "orange" });
  return platforms;
};

export const countConfigPlatforms = (config: IConfig): number => getConfigPlatforms(config).length;

/** True for a workspace-level configuration surfaced inside a PROJECT's list. */
export const isGlobalRow = (config: IConfig): boolean => config.isGlobalConfigOnProjectConfigList === true;

/**
 * A configuration the current surface may look at but not change.
 *
 * The tag alone is not the test. `fetchConfigs` sets it only while MERGING workspace
 * configurations into a project's list; the workspace surface reads the same rows straight
 * from `getGlobalConfigs()`, where they are untagged and entirely editable. Keying
 * read-only off the tag by itself would therefore lock the workspace out of its own
 * configurations the moment anything set it.
 */
export const isReadOnlyReference = (scope: ConfigScope, config: IConfig): boolean =>
  scope === "project" && isGlobalRow(config);

/**
 * The list a NEW name must be unique within, for a given surface: every configuration minus
 * the read-only global references a project's list merges in. Shared by the landing's create
 * dialog and the editor's own rename — a name the dialog would reject must never be one the
 * open configuration can be saved under, and vice versa.
 */
export const getConfigNameScope = (scope: ConfigScope, configs: IConfig[]): IConfig[] =>
  (configs ?? []).filter((config: IConfig): boolean => !isReadOnlyReference(scope, config));

/**
 * The list both left panels showed, reproduced exactly.
 *
 * `isRunConfig` rows are the hidden configuration the Run button writes to — neither panel
 * ever listed it, and listing it now would offer Delete on something the tool recreates.
 *
 * The Project/Global split belongs to the PROJECT surface alone. Only a project's list
 * holds both stores at once (`fetchConfigs` merges the workspace ones in), so only there is
 * there anything to split; the workspace surface reads one store and every row belongs to
 * it. Applying the bucket test there would filter out every untagged row — which is all of
 * them, since the tag is written during the merge and nowhere else.
 *
 * Where it does apply, the test is a strict `undefined` compare rather than a falsy one,
 * because that is what the panel did. A third "All" bucket would have to change this, which
 * is why there isn't one.
 */
export const filterConfigs = (
  configs: IConfig[],
  scope: ConfigScope,
  listType: ConfigScope,
  searchText: string
): IConfig[] => {
  const needle: string = searchText.trim().toLowerCase();
  return configs.filter((config: IConfig): boolean => {
    if (config.isRunConfig) return false;
    if (
      scope === "project" &&
      config.isGlobalConfigOnProjectConfigList !== (listType === "global" ? true : undefined)
    ) {
      return false;
    }
    if (!needle) return true;
    return nameOf(config).toLowerCase().indexOf(needle) !== -1;
  });
};

/**
 * Case-insensitive and deliberately NOT locale-aware: `toLowerCase` is kept exactly as the
 * pre-refactor validators had it, so which duplicates get rejected does not shift for a
 * Turkish-named project. Both sides are trimmed first — a name box does not trim as you
 * type, so "CMTest01" and " CMTest01" must collide or the check is only decorative.
 *
 * `selfId` excludes the row being renamed, so committing an unchanged name — or a
 * case-only change — is not reported as a collision with itself.
 */
export const findConfigNameConflict = (name: string, configs: IConfig[], selfId?: string): IConfig | undefined => {
  const needle: string = normalizeConfigName(name).toLowerCase();
  return configs.find(
    (config: IConfig): boolean => config.id !== selfId && normalizeConfigName(nameOf(config)).toLowerCase() === needle
  );
};

/** Options shared by `validateConfigName` and `configNameRules` — what a name is being checked against. */
export interface IConfigNameRuleOptions {
  /** The list the name must be unique within — see `getConfigNameScope`. */
  configs: IConfig[];
  /** Excludes the row being renamed from its own uniqueness check. */
  selfId?: string;
  /**
   * The name the configuration was saved under before this edit. A configuration saved
   * before this rule existed can already be too long, collide with another row, or carry
   * characters the pattern no longer allows — re-saving the form WITHOUT touching the name
   * must not lock the user out of SAVE over a violation that predates the rule.
   */
  originalName?: string;
}

/**
 * The one name rule, as a value rather than as antd `Rule`s — used directly by the submit
 * guard (which has no `Form.Item` to ask), and by `configNameRules` below for the field
 * itself. Returns the message to show, or `undefined` when the name is fine.
 *
 * Order matters: a blank name is reported as missing, never as a pattern failure or a
 * collision with another blank-named row.
 */
export const validateConfigName = (value: string | undefined, options: IConfigNameRuleOptions): string | undefined => {
  const normalized: string = normalizeConfigName(value ?? "");
  if (!normalized) return CONFIG_NAME_REQUIRED_MESSAGE;

  const originalNormalized: string | undefined =
    options.originalName !== undefined ? normalizeConfigName(options.originalName) : undefined;
  if (originalNormalized !== undefined && originalNormalized === normalized) return undefined;

  if (normalized.length > CONFIG_NAME_MAX_LENGTH) return CONFIG_NAME_LENGTH_MESSAGE;
  if (!CONFIG_NAME_PATTERN.test(normalized)) return CONFIG_NAME_MESSAGE;
  if (findConfigNameConflict(normalized, options.configs, options.selfId)) return CONFIG_NAME_CONFLICT_MESSAGE;
  return undefined;
};

/**
 * The one name rule, as antd `Rule`s — what the create dialog, the editor's inline rename,
 * and the list's Rename row action all mount on their `Form.Item`.
 *
 * Two rules, not one, so a blank value is never reported twice: `required`+`whitespace`
 * catches blank and whitespace-only on its own (antd's `whitespace` option treats an
 * all-spaces value as empty), and the async validator below skips a blank value rather than
 * re-running `validateConfigName` and re-throwing the same required message a second time.
 */
export const configNameRules = (options: IConfigNameRuleOptions): Rule[] => [
  { required: true, whitespace: true, message: CONFIG_NAME_REQUIRED_MESSAGE },
  {
    validator: async (_rule: unknown, value: string): Promise<void> => {
      const normalized: string = normalizeConfigName(value ?? "");
      if (!normalized) return;
      const error: string | undefined = validateConfigName(value, options);
      if (error) throw new Error(error);
    }
  }
];
